import React, { useEffect, useState, useContext } from 'react';
import {
  Box,
  Button,
  Typography,
  Input,
  Chip,
  Select,
  Option,
  Card,
  Grid,
  FormControl,
  FormLabel,
  Modal,
  ModalDialog,
  ModalClose,
  Avatar,
  IconButton,
  Textarea
} from '@mui/joy';
import { supabase } from '../supabaseClient';
import { AuthContext } from '../context/AuthContext.jsx';
import PageLayout from '../components/PageLayout';
import MetricCard from '../components/MetricCard';
import EnhancedButton from '../components/EnhancedButton';
import EnhancedInput from '../components/EnhancedInput';
import LoadingSkeleton, { CardSkeleton } from '../components/LoadingSkeleton';

const InternalDocs = () => {
  const [docs, setDocs] = useState([]);
  const [file, setFile] = useState(null);
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState('user');
  const { userMetadata } = useContext(AuthContext);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState('newest');
  const [fileTypeFilter, setFileTypeFilter] = useState('all');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // Helper functions
  const getFileIcon = (ext) => {
    switch (ext?.toLowerCase()) {
      case 'pdf':
        return 'picture_as_pdf';
      case 'doc':
      case 'docx':
        return 'description';
      case 'xls':
      case 'xlsx':
      case 'csv':
        return 'table_chart';
      case 'ppt':
      case 'pptx':
        return 'slideshow';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return 'image';
      case 'zip':
      case 'rar':
        return 'archive';
      case 'txt':
        return 'text_snippet';
      case 'dwg':
        return 'architecture';
      default:
        return 'insert_drive_file';
    }
  };

  const getFileTypeColor = (ext) => {
    switch (ext?.toLowerCase()) {
      case 'pdf':
        return '#dc3545';
      case 'doc':
      case 'docx':
        return '#007bff';
      case 'xls':
      case 'xlsx':
      case 'csv':
        return '#28a745';
      case 'ppt':
      case 'pptx':
        return '#fd7e14';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return '#6f42c1';
      case 'zip':
      case 'rar':
        return '#6c757d';
      case 'dwg':
        return '#00A884';
      default:
        return '#495057';
    }
  };

  const getFileTypeLabel = (ext) => {
    switch (ext?.toLowerCase()) {
      case 'pdf':
        return 'PDF Document';
      case 'doc':
      case 'docx':
        return 'Word Document';
      case 'xls':
      case 'xlsx':
        return 'Excel Spreadsheet';
      case 'csv':
        return 'CSV Data';
      case 'ppt':
      case 'pptx':
        return 'PowerPoint';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return 'Image';
      case 'zip':
      case 'rar':
        return 'Archive';
      case 'txt':
        return 'Text File';
      case 'dwg':
        return 'CAD Drawing';
      default:
        return 'Document';
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  useEffect(() => {
    const fetchUser = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      setUser(user);

      if (user?.id) {
        const { data, error } = await supabase
          .from('users')
          .select('user_type')
          .eq('id', user.id)
          .single();

        if (!error && data?.user_type) {
          setUserRole(data.user_type.toLowerCase());
        }
      }
    };

    fetchUser();
  }, []);

  useEffect(() => {
    fetchDocs();
  }, []);

  const fetchDocs = async () => {
    setInitialLoading(true);
    const { data, error } = await supabase
      .from('internal_documents')
      .select('*')
      .order('uploaded_at', { ascending: false });

    if (!error) setDocs(data || []);
    setInitialLoading(false);
  };

  const handleUpload = async () => {
    if (!file) return;
    setLoading(true);

    const path = `${Date.now()}_${file.name}`;
    const { error: uploadError } = await supabase.storage
      .from('internal-docs')
      .upload(path, file);

    if (uploadError) {
      console.error('Upload error:', uploadError);
      setLoading(false);
      return;
    }

    const { error: dbError } = await supabase.from('internal_documents').insert([
      {
        name: file.name,
        storage_path: path,
        uploaded_by: user?.id,
        description,
        tags: tags.split(',').map((tag) => tag.trim()),
        file_size: file.size,
        file_type: file.type,
      },
    ]);

    if (!dbError) {
      fetchDocs();
      setFile(null);
      setDescription('');
      setTags('');
      setUploadModalOpen(false);
    } else {
      console.error('Insert error:', dbError);
    }

    setLoading(false);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
      setUploadModalOpen(true);
    }
  };

  const handleDelete = async (doc) => {
    if (!doc?.id || !doc?.storage_path) {
      console.error('❌ Invalid document data:', doc);
      return;
    }

    console.log('🗑️ Attempting to delete document:', doc);

    // Delete from storage
    const { error: storageError } = await supabase.storage
      .from('internal-docs')
      .remove([doc.storage_path]);

    if (storageError) {
      console.error('❌ Storage deletion error:', storageError);
    } else {
      console.log('✅ File removed from Supabase Storage');
    }

    // Delete from database
    const { data: deletedRecord, error: dbError } = await supabase
      .from('internal_documents')
      .delete()
      .match({ id: doc.id });

    if (dbError) {
      console.error('❌ Failed to delete record from internal_documents table:', dbError);
    } else if (deletedRecord?.length === 0) {
      console.warn('⚠️ Delete attempted but no record matched');
    } else {
      console.log('✅ Document record deleted');
      // Remove from local state
      setDocs((prev) => prev.filter((d) => d.id !== doc.id));
    }
  };

  const downloadFile = async (path) => {
    const { data, error } = await supabase.storage
      .from('internal-docs')
      .createSignedUrl(path, 60);

    if (!error) {
      window.open(data.signedUrl, '_blank');
    }
  };

  // Analytics data
  const totalDocs = docs.length;
  const recentDocs = docs.filter(doc => {
    const uploadDate = new Date(doc.uploaded_at);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return uploadDate > thirtyDaysAgo;
  }).length;

  const fileTypes = docs.reduce((acc, doc) => {
    const ext = doc.name?.split('.').pop()?.toLowerCase() || 'unknown';
    acc[ext] = (acc[ext] || 0) + 1;
    return acc;
  }, {});

  const totalStorage = docs.reduce((total, doc) => total + (doc.file_size || 0), 0);

  // Filter and sort logic
  let filteredDocs = docs.filter(doc => {
    const matchesSearch = 
      (doc.description || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (doc.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (doc.tags || []).some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesFileType = fileTypeFilter === 'all' || 
      doc.name?.split('.').pop()?.toLowerCase() === fileTypeFilter;
    
    return matchesSearch && matchesFileType;
  });

  // Sort logic
  filteredDocs.sort((a, b) => {
    switch (sortOption) {
      case 'newest':
        return new Date(b.uploaded_at) - new Date(a.uploaded_at);
      case 'oldest':
        return new Date(a.uploaded_at) - new Date(b.uploaded_at);
      case 'name':
        return (a.name || '').localeCompare(b.name || '');
      case 'size':
        return (b.file_size || 0) - (a.file_size || 0);
      default:
        return 0;
    }
  });

  if (initialLoading) {
    return (
      <PageLayout title="Internal Documents">
        <Box sx={{ maxWidth: '1400px', mx: 'auto', p: 3 }}>
          {/* Analytics Loading */}
          <Box sx={{ mb: 4 }}>
            <LoadingSkeleton height="40px" width="250px" sx={{ mb: 3 }} />
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
              <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
              <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
              <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
            </Grid>
          </Box>
          
          {/* Controls Loading */}
          <CardSkeleton />
          
          {/* Documents Loading */}
          <Box sx={{ mt: 4 }}>
            <Grid container spacing={3}>
              {[...Array(8)].map((_, i) => (
                <Grid key={i} xs={12} sm={6} md={4} lg={3}><CardSkeleton /></Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout 
      title="Internal Documents"
      fullWidth
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
    >
      <Box sx={{ 
        maxWidth: '1400px', 
        mx: 'auto', 
        p: 3,
        minHeight: '100vh',
        background: 'linear-gradient(135deg, rgba(0, 168, 132, 0.02) 0%, rgba(248, 250, 252, 1) 100%)'
      }}>
        {/* Hero Header */}
        <Card 
          sx={{ 
            mb: 4,
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
            backdropFilter: 'blur(15px)',
            border: '1px solid',
            borderColor: 'success.200',
            borderRadius: 'xl',
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #00A884 0%, #007E33 100%)',
            }
          }}
          className="fade-in"
        >
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                mx: 'auto',
                mb: 2,
                background: 'linear-gradient(135deg, #00A884, #007E33)',
                color: 'white'
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '32px', fontFamily: 'Material Symbols Outlined' }}>
                folder_managed
              </span>
            </Avatar>
            
            <Typography 
              level="h3" 
              sx={{ 
                mb: 1, 
                background: 'linear-gradient(135deg, #00A884, #007E33)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: 700
              }}
            >
              Internal Document Library
            </Typography>
            
            <Typography level="body-md" sx={{ color: 'text.secondary', maxWidth: '600px', mx: 'auto' }}>
              Centralized repository for internal documents, resources, and shared files
            </Typography>
          </Box>
        </Card>

        {/* Analytics Overview */}
        <Card sx={{ mb: 4, borderRadius: 'xl', overflow: 'hidden' }} className="fade-in-up">
          <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'success.100', bgcolor: 'success.50' }}>
            <Typography 
              level="h4" 
              sx={{ 
                color: 'success.700',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                fontWeight: 700
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
                analytics
              </span>
              Library Statistics
            </Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 0.5 }}>
              Overview of your document repository and storage usage
            </Typography>
          </Box>
          
          <Box sx={{ p: 4 }}>
            <Grid container spacing={3}>
              <Grid xs={12} sm={6} md={3}>
                <MetricCard
                  title="Total Documents"
                  value={totalDocs}
                  icon="folder"
                  gradientColors={['#00A884', '#007E33']}
                />
              </Grid>
              <Grid xs={12} sm={6} md={3}>
                <MetricCard
                  title="Recent Uploads"
                  value={recentDocs}
                  icon="upload"
                  gradientColors={['#17a2b8', '#138496']}
                />
              </Grid>
              <Grid xs={12} sm={6} md={3}>
                <MetricCard
                  title="File Types"
                  value={Object.keys(fileTypes).length}
                  icon="category"
                  gradientColors={['#6f42c1', '#5a35a3']}
                />
              </Grid>
              <Grid xs={12} sm={6} md={3}>
                <MetricCard
                  title="Storage Used"
                  value={formatFileSize(totalStorage)}
                  icon="storage"
                  gradientColors={['#28a745', '#218838']}
                />
              </Grid>
            </Grid>
          </Box>
        </Card>

        {/* Upload & Controls */}
        <Card sx={{ mb: 4, borderRadius: 'xl', overflow: 'hidden' }} className="fade-in-up">
          <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'warning.100', bgcolor: 'warning.50' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography 
                  level="h4" 
                  sx={{ 
                    color: 'warning.700',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    fontWeight: 700
                  }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
                    tune
                  </span>
                  Document Management
                </Typography>
                <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  Search, filter, and upload documents to the library
                </Typography>
              </Box>
              
              <EnhancedButton
                gradient
                onClick={() => setUploadModalOpen(true)}
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                    cloud_upload
                  </span>
                }
              >
                Upload Document
              </EnhancedButton>
            </Box>
          </Box>
          
          <Box sx={{ p: 3 }}>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid xs={12} sm={4} md={3}>
                <Typography level="body-sm" sx={{ mb: 1, fontWeight: 500 }}>Search Documents</Typography>
                <EnhancedInput
                  placeholder="Search documents, tags..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  startDecorator={
                    <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                      search
                    </span>
                  }
                />
              </Grid>
              
              <Grid xs={12} sm={3} md={2}>
                <FormControl>
                  <FormLabel sx={{ fontWeight: 500 }}>File Type</FormLabel>
                  <Select
                    value={fileTypeFilter}
                    onChange={(e, val) => setFileTypeFilter(val)}
                    size="sm"
                    sx={{ '&:focus-within': { borderColor: 'success.500', boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)' }}}
                  >
                    <Option value="all">All Types</Option>
                    {Object.keys(fileTypes).map(type => (
                      <Option key={type} value={type}>
                        {getFileTypeLabel(type)} ({fileTypes[type]})
                      </Option>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid xs={12} sm={3} md={2}>
                <FormControl>
                  <FormLabel sx={{ fontWeight: 500 }}>Sort By</FormLabel>
                  <Select
                    value={sortOption}
                    onChange={(e, val) => setSortOption(val)}
                    size="sm"
                    sx={{ '&:focus-within': { borderColor: 'success.500', boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)' }}}
                  >
                    <Option value="newest">Newest First</Option>
                    <Option value="oldest">Oldest First</Option>
                    <Option value="name">Name (A-Z)</Option>
                    <Option value="size">File Size</Option>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            {searchQuery && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'success.50', borderRadius: 'md', border: '1px solid', borderColor: 'success.200' }}>
                <Typography level="body-sm" sx={{ color: 'success.700', fontWeight: 500 }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined', verticalAlign: 'middle', marginRight: '8px' }}>
                    search
                  </span>
                  Found {filteredDocs.length} document{filteredDocs.length !== 1 ? 's' : ''} matching "{searchQuery}"
                </Typography>
              </Box>
            )}
          </Box>
        </Card>

        {/* Document Grid */}
        <Box className="fade-in-up">
          {filteredDocs.length > 0 ? (
            <Grid container spacing={3}>
              {filteredDocs.map((doc) => {
                const fileExt = doc.name?.split('.').pop() || '';
                const fileIcon = getFileIcon(fileExt);
                const fileColor = getFileTypeColor(fileExt);
                
                return (
                  <Grid key={doc.id} xs={12} sm={6} md={4} lg={3}>
                    <Card 
                      sx={{ 
                        height: '100%',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 25px rgba(0, 168, 132, 0.15)',
                        }
                      }}
                      className="hover-lift"
                    >
                      {/* File Header */}
                      <Box 
                        sx={{ 
                          p: 2, 
                          background: `linear-gradient(135deg, ${fileColor}15 0%, ${fileColor}05 100%)`,
                          borderBottom: '1px solid',
                          borderColor: 'neutral.200',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2
                        }}
                      >
                        <Avatar 
                          sx={{ 
                            bgcolor: fileColor,
                            color: 'white',
                            width: 48,
                            height: 48
                          }}
                        >
                          <span 
                            className="material-symbols-outlined" 
                            style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}
                          >
                            {fileIcon}
                          </span>
                        </Avatar>
                        
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography 
                            level="title-sm" 
                            sx={{ 
                              color: 'success.700',
                              fontWeight: 600,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              mb: 0.5
                            }}
                          >
                            {doc.description || 'Untitled Document'}
                          </Typography>
                          <Chip size="sm" variant="soft" sx={{ bgcolor: fileColor + '20', color: fileColor }}>
                            {getFileTypeLabel(fileExt)}
                          </Chip>
                        </Box>
                      </Box>

                      {/* Document Details */}
                      <Box sx={{ p: 2 }}>
                        <Typography 
                          level="body-sm" 
                          sx={{ 
                            mb: 2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}
                        >
                          <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                            insert_drive_file
                          </span>
                          {doc.name}
                        </Typography>

                        {/* Tags */}
                        {doc.tags && doc.tags.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                            {(Array.isArray(doc.tags) ? doc.tags : doc.tags.split(',')).slice(0, 3).map((tag, i) => (
                              <Chip key={i} size="sm" variant="soft" color="neutral">
                                {tag.trim()}
                              </Chip>
                            ))}
                            {(Array.isArray(doc.tags) ? doc.tags : doc.tags.split(',')).length > 3 && (
                              <Chip size="sm" variant="soft" color="neutral">
                                +{(Array.isArray(doc.tags) ? doc.tags : doc.tags.split(',')).length - 3}
                              </Chip>
                            )}
                          </Box>
                        )}

                        {/* File Info */}
                        <Box sx={{ mb: 2 }}>
                          <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 0.5 }}>
                            📅 {new Date(doc.uploaded_at).toLocaleDateString()}
                          </Typography>
                          {doc.file_size && (
                            <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                              📊 {formatFileSize(doc.file_size)}
                            </Typography>
                          )}
                        </Box>

                        {/* Action Buttons */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <EnhancedButton
                            size="sm"
                            variant="outlined"
                            onClick={() => downloadFile(doc.storage_path)}
                            startDecorator={
                              <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                                download
                              </span>
                            }
                          >
                            Download
                          </EnhancedButton>
                          
                          {(userRole === 'admin' || userRole === 'super-admin') && (
                            <IconButton
                              size="sm"
                              color="danger"
                              variant="soft"
                              onClick={() => handleDelete(doc)}
                            >
                              <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                                delete
                              </span>
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Card sx={{ p: 6, textAlign: 'center', borderRadius: 'xl' }}>
              <Avatar
                sx={{
                  bgcolor: 'neutral.100',
                  color: 'neutral.600',
                  width: 80,
                  height: 80,
                  mx: 'auto',
                  mb: 3,
                  fontSize: '2.5rem'
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '40px', fontFamily: 'Material Symbols Outlined' }}>
                  {searchQuery ? 'search_off' : 'folder_open'}
                </span>
              </Avatar>
              <Typography level="h5" sx={{ color: 'neutral.600', mb: 1 }}>
                {searchQuery ? 'No Documents Found' : 'No Documents Yet'}
              </Typography>
              <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 3 }}>
                {searchQuery 
                  ? `No documents match your search for "${searchQuery}". Try adjusting your search terms or filters.`
                  : 'Start building your document library by uploading files. Drag and drop files anywhere or use the upload button.'
                }
              </Typography>
              {!searchQuery && (
                <EnhancedButton
                  gradient
                  onClick={() => setUploadModalOpen(true)}
                  startDecorator={
                    <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                      cloud_upload
                    </span>
                  }
                >
                  Upload First Document
                </EnhancedButton>
              )}
            </Card>
          )}
        </Box>

        {/* Drag & Drop Overlay */}
        {dragActive && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(0, 168, 132, 0.1)',
              backdropFilter: 'blur(4px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
            }}
          >
            <Card sx={{ p: 4, textAlign: 'center', border: '2px dashed', borderColor: 'success.500' }}>
              <span 
                className="material-symbols-outlined" 
                style={{ 
                  fontSize: '64px', 
                  color: '#00A884',
                  fontFamily: 'Material Symbols Outlined'
                }}
              >
                cloud_upload
              </span>
              <Typography level="h4" sx={{ color: 'success.700', mt: 2 }}>
                Drop files here to upload
              </Typography>
            </Card>
          </Box>
        )}

        {/* Upload Modal */}
        <Modal open={uploadModalOpen} onClose={() => setUploadModalOpen(false)}>
          <ModalDialog 
            sx={{ 
              maxWidth: '500px',
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
              backdropFilter: 'blur(15px)',
              border: '1px solid',
              borderColor: 'success.200',
              borderRadius: 'xl'
            }}
          >
            <ModalClose />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Avatar
                sx={{
                  bgcolor: 'success.500',
                  color: 'white',
                  width: 48,
                  height: 48
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
                  cloud_upload
                </span>
              </Avatar>
              <Box>
                <Typography level="h4" sx={{ color: 'success.700', fontWeight: 600 }}>
                  Upload Document
                </Typography>
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                  Add a new document to the library
                </Typography>
              </Box>
            </Box>
            
            <Grid container spacing={2}>
              <Grid xs={12}>
                <FormControl>
                  <FormLabel>Choose File</FormLabel>
                  <Input
                    type="file"
                    onChange={(e) => setFile(e.target.files[0])}
                    sx={{
                      '&:focus-within': {
                        borderColor: 'success.500',
                        boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)',
                      },
                    }}
                  />
                  {file && (
                    <Typography level="body-xs" sx={{ mt: 1, color: 'success.600' }}>
                      Selected: {file.name} ({formatFileSize(file.size)})
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              
              <Grid xs={12}>
                <EnhancedInput
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description of the document..."
                  required
                />
              </Grid>
              
              <Grid xs={12}>
                <EnhancedInput
                  label="Tags"
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder="tag1, tag2, tag3..."
                  helperText="Separate multiple tags with commas"
                />
              </Grid>
              
              <Grid xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                  <EnhancedButton
                    variant="outlined"
                    onClick={() => setUploadModalOpen(false)}
                  >
                    Cancel
                  </EnhancedButton>
                  <EnhancedButton
                    gradient
                    onClick={handleUpload}
                    loading={loading}
                    disabled={!file || !description.trim()}
                    startDecorator={
                      <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                        upload
                      </span>
                    }
                  >
                    Upload Document
                  </EnhancedButton>
                </Box>
              </Grid>
            </Grid>
          </ModalDialog>
        </Modal>
      </Box>
    </PageLayout>
  );
};

export default InternalDocs;